import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import type { IconName } from "../../assets/icons";
import { playClick } from "../../lib/sounds";
import { navigate, routes } from "../../lib/urls";
import { ClickButton } from "../ClickButton";
import { FooterAction } from "../FooterAction";
import { Icon } from "../Icon";
import { Keycap } from "../Keycap";
import { KeyboardHint } from "../KeyboardHint";
import { SearchContainer, SearchInput } from "../SearchField";
import { Text } from "../Text";

const Container = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const SectionHeader = styled("div")`
  display: flex;
  padding: 10px 8px 6px 8px;
  box-sizing: content-box;
  align-items: center;
  color: var(--text-secondary);
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  padding: 4px;
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-height: 40dvh;
  }
`;

const Row = styled(ClickButton)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
  flex-shrink: 0;
`;

type Suggestion = {
  id: string;
  icon: IconName;
  label: string;
  prompt: string;
};

export type SuggestionSet = {
  header: string;
  items: readonly Suggestion[];
};

export type SkillItem = {
  id: string;
  name: string;
  description: string;
};

type SlashCommand = {
  id: string;
  icon: IconName;
  label: string;
  run: () => void;
};

type FlatItem =
  | { type: "skill"; skill: SkillItem }
  | { type: "suggestion"; suggestion: Suggestion }
  | { type: "action"; action: SlashCommand };

type Props = {
  onSelect: (prompt: string) => void;
  onSelectSkill: (skill: SkillItem) => void;
  onDismiss: () => void;
  suggestions: SuggestionSet;
  skills: readonly SkillItem[];
  canCreateSkill: boolean;
  onCreateSkill: () => void;
};

export function SlashMenu({
  onSelect,
  onSelectSkill,
  onDismiss,
  suggestions,
  skills,
  canCreateSkill,
  onCreateSkill,
}: Props) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = query.trim().toLowerCase();
  const filteredSkills = q
    ? skills.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q)
      )
    : skills;
  const filteredSuggestions = q
    ? suggestions.items.filter((s) => s.label.toLowerCase().includes(q))
    : suggestions.items;
  const matches: FlatItem[] = [
    ...filteredSkills.map((skill) => ({ type: "skill" as const, skill })),
    ...filteredSuggestions.map((suggestion) => ({
      type: "suggestion" as const,
      suggestion,
    })),
  ];

  // When nothing matches, offer somewhere to go rather than a dead end.
  const emptyActions: SlashCommand[] = [];
  if (matches.length === 0) {
    emptyActions.push({
      id: "browse",
      icon: "skills",
      label: "Browse skills",
      run: () => navigate(routes.skills.list, { q: query }),
    });
    if (canCreateSkill) {
      emptyActions.push({
        id: "create",
        icon: "plus",
        label: "Create a skill",
        run: onCreateSkill,
      });
    }
  }

  const flat: FlatItem[] = [
    ...matches,
    ...emptyActions.map((action) => ({ type: "action" as const, action })),
  ];
  const flatRef = useRef(flat);
  flatRef.current = flat;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keep the highlight in range as the filtered list shrinks.
  useEffect(() => {
    setSelectedIndex((i) => Math.min(i, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  useEffect(() => {
    bodyRef.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (index: number) => {
      const item = flatRef.current[index];
      if (!item) return;
      if (item.type === "skill") {
        onSelectSkill(item.skill);
      } else if (item.type === "suggestion") {
        onSelect(item.suggestion.prompt);
      } else {
        item.action.run();
      }
      onDismiss();
    },
    [onSelect, onSelectSkill, onDismiss]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex((i) => Math.min(i + 1, flatRef.current.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopImmediatePropagation();
        playClick();
        handleSelect(selectedIndex);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onDismiss();
      } else if (e.key === "Backspace" && query === "") {
        onDismiss();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [selectedIndex, handleSelect, onDismiss, query]);

  let rowIndex = 0;
  const renderRow = (key: string, icon: IconName, label: string) => {
    const index = rowIndex++;
    return (
      <Row
        key={key}
        data-no-pill
        onMouseEnter={() => setSelectedIndex(index)}
        onClick={() => handleSelect(index)}
        data-highlighted={index === selectedIndex}
      >
        <Icon name={icon} size={16} />
        <Text variant="text-regular" color="var(--text-primary)">
          {label}
        </Text>
        {index === selectedIndex && (
          <KeyboardHint style={{ position: "absolute", right: 8, top: 8 }}>
            <Keycap>
              <Icon name="corner-down-left" size={12} />
            </Keycap>
          </KeyboardHint>
        )}
      </Row>
    );
  };

  return (
    <Container>
      <Body ref={bodyRef}>
        {filteredSkills.length > 0 && (
          <>
            <SectionHeader>
              <Text variant="text-small">Skills</Text>
            </SectionHeader>
            {filteredSkills.map((skill) =>
              renderRow(`skill-${skill.id}`, "skills", skill.name)
            )}
          </>
        )}
        {filteredSuggestions.length > 0 && (
          <>
            <SectionHeader>
              <Text variant="text-small">{suggestions.header}</Text>
            </SectionHeader>
            {filteredSuggestions.map((suggestion) =>
              renderRow(
                `suggestion-${suggestion.id}`,
                suggestion.icon,
                suggestion.label
              )
            )}
          </>
        )}
        {emptyActions.map((action) =>
          renderRow(`action-${action.id}`, action.icon, action.label)
        )}
      </Body>
      <SearchContainer
        style={{
          borderTop: "0.5px solid var(--border-alpha)",
          borderBottom: "none",
        }}
      >
        <Icon name="search" size={16} />
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder="Search skills and prompts…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Close</Text>
            <KeyboardHint>
              <Keycap style={{ padding: "2px 4px" }}>
                <Text variant="text-smaller-md">esc</Text>
              </Keycap>
            </KeyboardHint>
          </FooterAction>
        </Instructions>
      </SearchContainer>
    </Container>
  );
}
